<script lang="ts">
	import { format } from 'svelte-i18n';
	import { devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
	import { deriveRoomName } from 'src/helpers/service_rooms_helpers';
	import { capFirstChar } from 'src/helpers/utilities';

	import type { Device } from 'src/types/Device';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import type { ListType } from 'src/components/SelectList/SelectList.types';
	import { Container } from '@Shelly/shelly-design-system';
	import EmptyDeviceTypeList from 'src/components/Lists/EmptyDeviceTypeList.svelte';
	import { sortRoomsWithMissingDevices } from 'src/components/Lists/list-helpers';
	import { isProThreeEM, real_id } from 'src/helpers/interface_helpers';
	import { hasSwitchPeripheral } from 'src/helpers/addons/addon_device_helpers';
	import { getDeviceInGroup, isGroupSupported } from 'src/helpers/group_helpers';

	export let editGroup;
	export let selectedFirstDevice: HTMLElement = null;

	let listDevByGroupType = [];
	let allCardsRefs: { [key: string | number]: HTMLElement } = {};
	let mappedListDevicesByRoomID: ListType = [];

	const cachedCurrentGroupDevices = editGroup.devices;

	$: listDevByGroupType = [
		...Object.values($devices).filter((d: Device) => {
			const groupType = editGroup.type;
			return isGroupSupported(d, groupType);
		}),
	];

	$: mappedListDevicesByRoomID = filterDevicesByRoomID(listDevByGroupType);

	$: selectedFirstDevice = editGroup.devices ? allCardsRefs[editGroup.devices[0]] : null;

	const filterDevicesByRoomID: any = (array: Device[]) => {
		let filteredDevicesByRoom = {};

		const deletedDevices = cachedCurrentGroupDevices.filter(
			(dId) =>
				'undefined' === typeof $devices[dId] ||
				//The Pro 3EM device exists but does not have an Add-on.
				(isProThreeEM($devices[dId]?.type) && !hasSwitchPeripheral(dId)),
		);

		array.forEach((device: Device, i) => {
			device = getDeviceInGroup(device);
			const roomID = device.room_id;
			const deviceData = {
				name: `${device.name}${
					!devices_status.getByRealId(real_id(device.id))?.online
						? `- ${capFirstChar($format('label_offline'))}`
						: ''
				}`,
				id: device.id,
				selected: editGroup.devices.findIndex((el) => el === device.id) !== -1,
				roomName: deriveRoomName(roomID),
				roomID: roomID,
				image: device.image ? device.image : undefined,
				icon: device.icon ? device.icon : undefined,
				backgroundColor: device?.backgroundColor ? device?.backgroundColor : undefined,
			};

			if (filteredDevicesByRoom[roomID]) {
				filteredDevicesByRoom[roomID].cards = [
					...filteredDevicesByRoom[roomID].cards,
					deviceData,
				];
			} else {
				filteredDevicesByRoom[roomID] = {
					cards: [deviceData],
					roomName: deriveRoomName(roomID),
					roomID: roomID,
				};
			}
		});

		if (deletedDevices.length) {
			deletedDevices.forEach((id) => {
				const deviceData = {
					name: `${$format('device_status_deleted', { default: 'deleted' })}: ${$devices[id]?.name || id}`,
					id,
					selected: editGroup.devices.findIndex((el) => el === id) !== -1,
					roomName: capFirstChar($format('deleted_devices', { default: 'deleted devices' })),
					roomID: -99,
					icon: 'text-warning fas fa-warning',
				};

				if (filteredDevicesByRoom[-99]) {
					filteredDevicesByRoom[-99].cards = [...filteredDevicesByRoom[-99].cards, deviceData];
				} else {
					filteredDevicesByRoom[-99] = {
						cards: [deviceData],
						roomName: $format('missing_devices_label', {
							default: 'Missing Devices',
						}),
						roomID: -99,
					};
				}
			});
		}

		return sortRoomsWithMissingDevices(filteredDevicesByRoom);
	};

	const onSelecDeviceInGroup = (device) => {
		const currendDevID = device.id;
		const currenDeviceRoomID = device.roomID;

		mappedListDevicesByRoomID = mappedListDevicesByRoomID.map((room: any) => {
			if (room.roomID === currenDeviceRoomID) {
				return {
					...room,
					cards: room.cards.map((d) => {
						if (d.id === currendDevID) {
							// set devices in group
							if (!d.selected) {
								editGroup.devices = [...editGroup.devices, d.id];
							} else {
								editGroup.devices = editGroup.devices.filter(
									(deviceID) => deviceID !== d.id,
								);
							}

							editGroup = editGroup;

							return {
								...d,
								selected: !d.selected,
							};
						}
						return d;
					}),
				};
			}

			return room;
		});
	};
</script>

<Container noScroll padding="0">
	{#if mappedListDevicesByRoomID.length > 0}
		<SelectList
			missingDevicesAlertHeading={$format('missing_devices_alert_label', {
				values: { type: $format('process_delete_group', { default: 'group' }) },
				default: 'You have missing device(s) in this {type}.',
			})}
			title={$format('label_selected', {
				default: 'Selected:',
			})}
			titleHighlight={editGroup.devices.length}
			list={mappedListDevicesByRoomID}
			bind:allCardsRefs
			on:click={(e) => onSelecDeviceInGroup(e.detail)}
		/>
	{:else}
		<EmptyDeviceTypeList type="modal" withoutMargin />
	{/if}
</Container>
